import React, { useRef, memo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ClipboardList, Briefcase, Network, Brain, Building, Calendar, Sliders, User, Building2 } from 'lucide-react';
// An animated relationship graph for the "Network" card. Connection lines
// draw themselves in from the center, a soft data-pulse travels along each
// edge toward the avatar on a loop, and every node gently floats and fades in
// with a stagger — so the card reads as a living, breathing map of the user's
// relationships rather than a static diagram. Respects reduced motion.
const FOUNDER_PHOTO_URL = "/ChatGPT_Image_Apr_24,_2026,_12_02_55_PM_3.png";
type GraphNode = {
  id: string;
  // x/y is the exact center of the node. The connector line points directly
  // to this center, and the pill is translated -50% -50% to sit perfectly
  // balanced over the line endpoint.
  x: number;
  y: number;
  type: 'person' | 'company';
  delay: number;
};
// Nodes are placed symmetrically around the centered hub (50/50): a balanced
// 2-2-2 layout matching the reference screenshot.
const NETWORK_NODES: GraphNode[] = [{
  id: 'a',
  x: 28,
  y: 22,
  type: 'person',
  delay: 0.15
}, {
  id: 'b',
  x: 72,
  y: 22,
  type: 'company',
  delay: 0.3
}, {
  id: 'c',
  x: 20,
  y: 50,
  type: 'company',
  delay: 0.45
}, {
  id: 'd',
  x: 80,
  y: 50,
  type: 'person',
  delay: 0.6
}, {
  id: 'e',
  x: 28,
  y: 78,
  type: 'person',
  delay: 0.75
}, {
  id: 'f',
  x: 72,
  y: 78,
  type: 'company',
  delay: 0.9
}];
const CENTER = {
  x: 50,
  y: 50
};
function NetworkGraph() {
  const reduced = !!useReducedMotion();
  return <div className="relative w-full h-full" data-id="element-3594" data-mp="38cmvgm9uh19w">
      <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" aria-hidden="true" data-id="element-3595" data-mp="38blbfsaup4g1">
        {/* Concentric background circles */}
        <circle cx="50%" cy="50%" r="18%" stroke="rgba(43,33,26,0.06)" strokeWidth="1" fill="none" data-id="element-3596" data-mp="4pw8ifkaiz91" />
        <circle cx="50%" cy="50%" r="32%" stroke="rgba(43,33,26,0.06)" strokeWidth="1" fill="none" data-id="element-3597" data-mp="3w2qmzv9rdakj" />
        <circle cx="50%" cy="50%" r="46%" stroke="rgba(43,33,26,0.06)" strokeWidth="1" fill="none" data-id="element-3598" data-mp="ivo7qwq1w5cl" />

        {NETWORK_NODES.map((n, i) => <g key={`edge-${n.id}`} data-id="element-3599" data-mp="nvka6rj3noq1" data-mpi={i}>
            <motion.line x1={`${CENTER.x}%`} y1={`${CENTER.y}%`} x2={`${n.x}%`} y2={`${n.y}%`} stroke="rgba(43,33,26,0.08)" strokeWidth={1} strokeLinecap="round" initial={reduced ? false : {
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 0.9,
          delay: reduced ? 0 : 0.2 + i * 0.12,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-3600" data-mp="a2kxsyem6amp" />
          </g>)}
      </svg>

      {NETWORK_NODES.map(n => <div key={n.id} className="absolute" style={{
      top: `${n.y}%`,
      left: `${n.x}%`,
      // Center the wrapper exactly on its coordinate so the connector line
      // points to its true center, ensuring perfect visual symmetry.
      // We apply this to a static wrapper so Framer Motion doesn't overwrite it.
      transform: 'translate(-50%, -50%)'
    }} data-id="element-3601" data-mp="3r9n1drpjvemx">
          <motion.div className="flex items-center gap-2.5 rounded-xl border border-neutral-100 bg-white p-1.5 shadow-sm" initial={reduced ? false : {
        opacity: 0,
        scale: 0.6
      }} animate={reduced ? {
        opacity: 1
      } : {
        opacity: 1,
        scale: 1,
        y: [0, -4, 0]
      }} transition={{
        opacity: {
          duration: 0.5,
          delay: n.delay
        },
        scale: {
          type: 'spring',
          stiffness: 260,
          damping: 18,
          delay: n.delay
        },
        y: {
          duration: 4 + n.delay,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: n.delay
        }
      }} data-id="element-3602" data-mp="39yhvusyipzwa">
            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${n.type === 'person' ? 'bg-[#FFF4ED] text-[#EA580C]' : 'bg-[#F5F5F4] text-[#78716C]'}`} data-id="element-3603" data-mp="1j7uxo6d70q3">
              {n.type === 'person' ? <User size={14} strokeWidth={2.5} data-id="element-3604" data-mp="23es0rz5sx10n" /> : <Building2 size={14} strokeWidth={2.5} data-id="element-3605" data-mp="25ru2isl5fm4g" />}
            </span>
            <div className="flex flex-col gap-1.5 pr-3" data-id="element-3606" data-mp="29se0tzhgkvs6">
              <span className="h-1.5 w-11 rounded-full bg-neutral-200/70" data-id="element-3607" data-mp="29r3068jb845a" />
              <span className="h-1.5 w-7 rounded-full bg-neutral-200/70" data-id="element-3608" data-mp="2essblpag2jtw" />
            </div>
          </motion.div>
        </div>)}

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-3609" data-mp="2jq27nnsc9tqp">
        <div className="relative flex h-16 w-16 items-center justify-center" data-id="element-3610" data-mp="1m4o3kx4ehuo1">
          {!reduced && <>
              {/* Soft glow halo that gently breathes behind the hub.
               Using marginLeft/marginTop instead of translate classes so Framer Motion
               doesn't overwrite the centering when it animates scale. */}
              <motion.span className="absolute left-1/2 top-1/2 rounded-full" style={{
            width: 110,
            height: 110,
            marginLeft: -55,
            marginTop: -55,
            background: 'radial-gradient(circle, rgba(90,40,15,0.4) 0%, rgba(90,40,15,0) 60%)'
          }} animate={{
            scale: [1, 1.15, 1],
            opacity: [0.5, 0.8, 0.5]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-3611" data-mp="13clgp4161i5c" />
            </>}
          <motion.div className="relative flex h-16 w-16 items-center justify-center rounded-full border-[3px] border-white shadow-[0_4px_16px_-4px_rgba(0,0,0,0.2)] overflow-hidden bg-white z-10" initial={reduced ? false : {
          scale: 0,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 220,
          damping: 18,
          delay: reduced ? 0 : 0.1
        }} aria-label="You" data-id="element-3612" data-mp="lvemkxa0e4sf">
            <motion.div animate={reduced ? undefined : {
            scale: [1, 1.06, 1]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-full h-full" data-id="element-3613" data-mp="3u9wyxb6zg6hu">
              <img src={FOUNDER_PHOTO_URL} alt="You" className="w-full h-full object-cover" data-id="element-3614" data-mp="3kmufrrnuvpoh" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>;
}
export function MemoryCarousel({
  headline = 'Giga gets smarter with every interaction.',
  subhead = "It learns your rhythm over time, so every interaction builds a deeper understanding of exactly how you work — including who's in your orbit and their roles."
}: {
  headline?: React.ReactNode;
  subhead?: React.ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  return <section className="w-full bg-[#FAF8F4] py-24 md:py-32 overflow-hidden border-y border-neutral-200/70" data-id="element-3615" data-mp="3plxb2ke1ahj9">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-3616" data-mp="3uj5s2hng4fix">
        {/* Header */}
        <div className="flex flex-col items-start md:items-center text-left md:text-center mb-12 md:mb-16" data-id="element-3617" data-mp="m45ltdra8bor">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-title font-medium text-[#2B211A] tracking-tight mb-4" data-id="element-3618" data-mp="qy9ty0caaprf">
            {"Smarter with every interaction"}
          </h2>
          <p className="text-base md:text-lg text-neutral-500 font-medium max-w-2xl" data-id="element-3619" data-mp="w8vsfwxms3zb">
            {"Giga learns your rhythm over time, so every interaction builds a deeper understanding of exactly how you work."}
          </p>
        </div>
      </div>

      {/* Carousel */}
      <div className="w-full relative" data-id="element-3620" data-mp="hbcxgcud0ujf">
        {/* Fade edges */}
        <div className="absolute right-0 top-0 bottom-0 w-8 md:w-24 bg-gradient-to-l from-[#FAF8F4] to-transparent z-10 pointer-events-none" data-id="element-3621" data-mp="daj95id09dq0" />

        <div ref={scrollRef} className="flex gap-4 md:gap-6 overflow-x-auto pb-8 pt-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory" style={{
        paddingLeft: 'max(1.5rem, calc((100% - 1280px) / 2 + 1.5rem))',
        paddingRight: 'max(1.5rem, calc((100% - 1280px) / 2 + 1.5rem))',
        scrollPaddingLeft: 'max(1.5rem, calc((100% - 1280px) / 2 + 1.5rem))'
      }} data-id="element-3622" data-mp="rb3qsb76p3hx">
          {/* 1. Priorities */}
          <div className="snap-start shrink-0 w-[280px] md:w-[320px] h-[400px] bg-white border border-neutral-200/70 shadow-sm rounded-2xl p-6 md:p-8 flex flex-col transition-transform hover:-translate-y-1 hover:border-neutral-300 cursor-pointer group" data-id="element-3623" data-mp="3s4ryf41ijjjd">
            <div className="flex items-center gap-3 mb-6" data-id="element-3624" data-mp="9tnbcu5xg75n">
              <ClipboardList className="text-[#EA580C]" size={20} data-id="element-3625" data-mp="erovlif3acip" />
              <h3 className="text-lg font-bold text-neutral-900" data-id="element-3626" data-mp="erf6ry5s6bel">Priorities</h3>
            </div>
            <div className="flex flex-col gap-5 text-sm text-neutral-500 leading-relaxed" data-id="element-3627" data-mp="joyj2six4e3w">
              <p data-id="element-3628" data-mp="nswh0zwibivd">
                Ship the new AI-forward landing page and make the product story
                sharper.
              </p>
              <p data-id="element-3629" data-mp="2pjrj29hb4eaj">
                Keep pilots, investor conversations, and product feedback moving
                with timely follow-ups.
              </p>
              <p className="opacity-50" data-id="element-3630" data-mp="2ujcon2qzfkw8">Turn conversations into records.</p>
            </div>
          </div>

          {/* 2. Work */}
          <div className="snap-start shrink-0 w-[280px] md:w-[320px] h-[400px] bg-white border border-neutral-200/70 shadow-sm rounded-2xl p-6 md:p-8 flex flex-col transition-transform hover:-translate-y-1 hover:border-neutral-300 cursor-pointer group" data-id="element-3631" data-mp="2i8lfupw3ptob">
            <div className="flex items-center gap-3 mb-6" data-id="element-3632" data-mp="28bqlmj6v2ffu">
              <Briefcase className="text-[#EA580C]" size={20} data-id="element-3633" data-mp="35ypjgx9ahtfb" />
              <h3 className="text-lg font-bold text-neutral-900" data-id="element-3634" data-mp="35w3aquanx7vi">Work</h3>
            </div>
            <div className="flex flex-col gap-5" data-id="element-3635" data-mp="30zw1osmyzg9c">
              <div className="flex items-start gap-3" data-id="element-3636" data-mp="2vzgpi4efcesy">
                <svg className="w-4 h-4 mt-0.5 text-[#EA4335] shrink-0" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" data-id="element-3637" data-mp="2w22z09kvl4dt">
                  <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" data-id="element-3638" data-mp="1kl36pdyj2pjs" />
                </svg>
                <div data-id="element-3639" data-mp="1f7a3smhh4zye">
                  <p className="text-sm font-semibold text-neutral-800 mb-1" data-id="element-3640" data-mp="1f81sregkfzfp">
                    Re: Mastra pilot next steps
                  </p>
                  <p className="text-xs text-neutral-500 line-clamp-2" data-id="element-3641" data-mp="1t09mexcjzaa6">
                    Alex confirmed the eval scope and asked for Friday
                    implementation...
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-3642" data-mp="1xyb35bmiaxih">
                <div className="w-4 h-4 mt-0.5 rounded bg-[#D4FF00] flex items-center justify-center shrink-0" data-id="element-3643" data-mp="1xzujt3c2raks">
                  <span className="text-[10px] font-bold text-black" data-id="element-3644" data-mp="26y6ilsn3heob">G</span>
                </div>
                <div data-id="element-3645" data-mp="hj9x8ali5bkd">
                  <p className="text-sm font-semibold text-neutral-800 mb-1" data-id="element-3646" data-mp="higk8rvy9pts">
                    Granola notes - Corgi found...
                  </p>
                  <p className="text-xs text-neutral-500 line-clamp-2" data-id="element-3647" data-mp="cm9p7u53dyqm">
                    Prep should cover pilot usage, open blockers, and the
                    renewal...
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 opacity-50" data-id="element-3648" data-mp="2ffqdnk8zvybc">
                <svg className="w-4 h-4 mt-0.5 shrink-0" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-3649" data-mp="2fddz7h2g4631">
                  <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52z" fill="#E01E5A" data-id="element-3650" data-mp="25h0wjtoxahw5" />
                  <path d="M6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-3651" data-mp="2ahovr2nb251g" />
                  <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834z" fill="#36C5F0" data-id="element-3652" data-mp="20ipkrdf2hxba" />
                  <path d="M8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0" data-id="element-3653" data-mp="3uvyvt9n9hqvn" />
                  <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.52 2.528 2.528 0 0 1 2.521 2.52 2.528 2.528 0 0 1-2.521 2.522h-2.522V8.833z" fill="#2EB67D" data-id="element-3654" data-mp="17l5auxkgk1cc" />
                  <path d="M17.688 8.834a2.528 2.528 0 0 1-2.523 2.522 2.528 2.528 0 0 1-2.52-2.522V2.522A2.528 2.528 0 0 1 17.688 0a2.528 2.528 0 0 1 2.522 2.522v6.312z" fill="#2EB67D" data-id="element-3655" data-mp="ynml5fvvcyu4" />
                  <path d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522 2.528 2.528 0 0 1-2.523 2.521 2.528 2.528 0 0 1-2.52-2.521v-2.522h2.52z" fill="#ECB22E" data-id="element-3656" data-mp="1qcy3urzj1mk2" />
                  <path d="M15.165 17.688a2.528 2.528 0 0 1-2.523-2.523 2.528 2.528 0 0 1 2.523-2.52 2.528 2.528 0 0 1 2.52 2.52v6.313a2.528 2.528 0 0 1-2.52 2.522 2.528 2.528 0 0 1-2.523-2.522v-6.313z" fill="#ECB22E" data-id="element-3657" data-mp="1hlpci1bw37v3" />
                </svg>
                <div data-id="element-3658" data-mp="2a2s13ll0gtvo">
                  <p className="text-sm font-semibold text-neutral-800 mb-1" data-id="element-3659" data-mp="2a3b9aakv03wi">
                    #product - launch page fee...
                  </p>
                  <p className="text-xs text-neutral-500 line-clamp-2" data-id="element-3660" data-mp="2cmy33nq2ea9l">
                    Dana flagged the integrations section spacing and asked for
                    a...
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Network */}
          <div className="snap-start shrink-0 w-[280px] md:w-[320px] h-[400px] bg-white border border-neutral-200/70 shadow-sm rounded-2xl p-6 md:p-8 flex flex-col transition-transform hover:-translate-y-1 hover:border-neutral-300 cursor-pointer group relative overflow-hidden" data-id="element-3661" data-mp="2qmpf5ix0tgky">
            <div className="flex items-center gap-3 mb-6 relative z-10" data-id="element-3662" data-mp="35b3swovz6pga">
              <Network className="text-[#EA580C]" size={20} data-id="element-3663" data-mp="35d6pjrrmvgtz" />
              <h3 className="text-lg font-bold text-neutral-900" data-id="element-3664" data-mp="3a958v0y3u8nj">Network</h3>
            </div>
            {/* Animated relationship graph */}
            <div className="absolute inset-0 top-16 pb-6 px-2 pointer-events-none" data-id="element-3665" data-mp="3e9p7ya28nmcb">
              <NetworkGraph data-id="element-3666" data-mp="3jb4p3r1ap8pf" />
            </div>
          </div>

          {/* 4. Memory */}
          <div className="snap-start shrink-0 w-[280px] md:w-[320px] h-[400px] bg-white border border-neutral-200/70 shadow-sm rounded-2xl p-6 md:p-8 flex flex-col transition-transform hover:-translate-y-1 hover:border-neutral-300 cursor-pointer group" data-id="element-3667" data-mp="35ml6lpobn1bo">
            <div className="flex items-center gap-3 mb-6" data-id="element-3668" data-mp="3aecnt9ll7up5">
              <Brain className="text-[#EA580C]" size={20} data-id="element-3669" data-mp="3agxqg0tpmeny" />
              <h3 className="text-lg font-bold text-neutral-900" data-id="element-3670" data-mp="2mqbhm7bvsqcw">Memory</h3>
            </div>
            <div className="flex flex-col gap-5 text-sm text-neutral-500 leading-relaxed" data-id="element-3671" data-mp="2ioggb2tyacz4">
              <p data-id="element-3672" data-mp="2wnpz8a9jjl6s">
                <span className="text-neutral-500 block text-xs mb-0.5" data-id="element-3673" data-mp="2wo97ez9e2v7m">
                  11:21 AM 6/2/25
                </span>
                Faith wanted examples of memories that Giga can keep current for
                her.
              </p>
              <p data-id="element-3674" data-mp="6y4ic3jpai16">
                <span className="text-neutral-500 block text-xs mb-0.5" data-id="element-3675" data-mp="6ynnejo95bxs">
                  11:10 AM 6/2/25
                </span>
                Faith prefers landing copy that is direct, specific, and
                AI-forward.
              </p>
              <p className="opacity-50" data-id="element-3676" data-mp="1tw5c159hxdo1">
                <span className="text-neutral-500 block text-xs mb-0.5" data-id="element-3677" data-mp="1fvcfyg3jqait">
                  10:42 AM 6/2/25
                </span>
                Giga should follow up with Corgi and Mastra about pilot next
                steps.
              </p>
            </div>
          </div>

          {/* 5. My Company */}
          <div className="snap-start shrink-0 w-[280px] md:w-[320px] h-[400px] bg-white border border-neutral-200/70 shadow-sm rounded-2xl p-6 md:p-8 flex flex-col transition-transform hover:-translate-y-1 hover:border-neutral-300 cursor-pointer group" data-id="element-3678" data-mp="2clv3pjhypjbq">
            <div className="flex items-center gap-3 mb-6" data-id="element-3679" data-mp="23qx987z61yia">
              <Building className="text-[#EA580C]" size={20} data-id="element-3680" data-mp="2rhktlsq9qglq" />
              <h3 className="text-lg font-bold text-neutral-900" data-id="element-3681" data-mp="2w9cepnqnnu4h">My Company</h3>
            </div>
            <div className="flex flex-col gap-5 text-sm text-neutral-500 leading-relaxed" data-id="element-3682" data-mp="365b27thz7677">
              <p data-id="element-3683" data-mp="362ymlmzox7xb">
                Giga is an AI CRM and work system for email, relationships,
                meetings, tasks, and documents.
              </p>
              <p data-id="element-3684" data-mp="2w5k8vv55ix8p">
                The product replaces scattered apps with one assistant that
                understands context and can act on it.
              </p>
              <p className="opacity-50" data-id="element-3685" data-mp="2n6yr427i7m8z">
                Current focus is memory, integrations, and proactive
                automations.
              </p>
            </div>
          </div>

          {/* 6. Meetings */}
          <div className="snap-start shrink-0 w-[280px] md:w-[320px] h-[400px] bg-white border border-neutral-200/70 shadow-sm rounded-2xl p-6 md:p-8 flex flex-col transition-transform hover:-translate-y-1 hover:border-neutral-300 cursor-pointer group" data-id="element-3686" data-mp="2dhoyml8r01kc">
            <div className="flex items-center gap-3 mb-6" data-id="element-3687" data-mp="1zgupu1hsfyl5">
              <Calendar className="text-[#EA580C]" size={20} data-id="element-3688" data-mp="1zhe9bmn36v1a" />
              <h3 className="text-lg font-bold text-neutral-900" data-id="element-3689" data-mp="ezlkgl4p194v">Meetings</h3>
            </div>
            <div className="flex flex-col gap-5 text-sm text-neutral-500 leading-relaxed" data-id="element-3690" data-mp="izvzenagr9vv">
              <p data-id="element-3691" data-mp="nzrws3ono32g">
                Prefers short prep briefs with relationship history, open asks,
                and the likely next step.
              </p>
              <p data-id="element-3692" data-mp="sxtlp58riwlu">
                Avoid scheduling over deep work blocks unless the meeting is
                customer or investor critical.
              </p>
              <p className="opacity-50" data-id="element-3693" data-mp="95vcv95ph1f">
                After calls, extract owners, follow-ups, decision points, and
                CRM updates.
              </p>
            </div>
          </div>

          {/* 7. Preferences */}
          <div className="snap-start shrink-0 w-[280px] md:w-[320px] h-[400px] bg-white border border-neutral-200/70 shadow-sm rounded-2xl p-6 md:p-8 flex flex-col transition-transform hover:-translate-y-1 hover:border-neutral-300 cursor-pointer group" data-id="element-3694" data-mp="a11w6e2mnl9c">
            <div className="flex items-center gap-3 mb-6" data-id="element-3695" data-mp="270zym2p67gzl">
              <Sliders className="text-[#EA580C]" size={20} data-id="element-3696" data-mp="273m4zz0brqg8" />
              <h3 className="text-lg font-bold text-neutral-900" data-id="element-3697" data-mp="223q96n1s75br">
                Preferences
              </h3>
            </div>
            <div className="flex flex-col gap-5 text-sm text-neutral-500 leading-relaxed" data-id="element-3698" data-mp="2g03e6wp4pe9c">
              <p data-id="element-3699" data-mp="2b1s6arie9dtr">
                Write in a direct, warm style with concise context and clear
                asks.
              </p>
              <p data-id="element-3700" data-mp="2l1j0es31lqtb">
                Prefer fast drafts, low ceremony, and reasoning only when it
                changes the decision.
              </p>
              <p className="opacity-50" data-id="element-3701" data-mp="2ys61us06tut3">
                Default to action when confidence is high, but ask before
                sensitive sends or external comms.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}